import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { MediaShowcaseItem } from '../../types.ts';
import {
  Image as ImageIcon,
  ExternalLink,
  ArrowRight,
  Maximize2,
  X,
  Star,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface MediaShowcaseSectionProps {
  badge?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonEnabled?: boolean;
}

export const DEFAULT_MEDIA_SHOWCASE: MediaShowcaseItem[] = [
  {
    id: 'media-1',
    imageUrl: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1400&q=85',
    title: 'ESG Forum & Sustainability Summit',
    caption: 'Strategic conference bringing together climate innovators, corporate leaders, and academic researchers.',
    badge: 'FEATURED • SUMMIT',
    order: 1,
  },
  {
    id: 'media-2',
    imageUrl: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1000&q=80',
    title: 'Clean Energy & Field Operations',
    caption: 'Hands-on training and verification of renewable installations across institutional hubs.',
    badge: 'FIELD LAB',
    order: 2,
  },
  {
    id: 'media-3',
    imageUrl: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=1000&q=80',
    title: 'Corporate Decarbonization Workshop',
    caption: 'Interactive workshops helping organizations architect verified carbon accounting roadmaps.',
    badge: 'WORKSHOP',
    order: 3,
  },
  {
    id: 'media-4',
    imageUrl: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1000&q=80',
    title: 'Green Innovation & Research Hub',
    caption: 'Advanced climate modeling and environmental engineering collaborative workspaces.',
    badge: 'RESEARCH',
    order: 4,
  },
];

export default function MediaShowcaseSection({
  badge = 'VISUAL HIGHLIGHTS & MEDIA',
  title = 'Media & Featured Gallery',
  subtitle = 'Memorable moments, field research documentation, and corporate summit highlights',
  buttonText = 'Explore Full Media Gallery →',
  buttonEnabled = true,
}: MediaShowcaseSectionProps) {
  const { settings, navigate } = useApp();
  const primaryColor = settings?.theme?.primaryColor || '#059669';

  const mediaList: MediaShowcaseItem[] =
    settings?.mediaShowcase && settings.mediaShowcase.length > 0
      ? [...settings.mediaShowcase].sort((a, b) => (a.order || 0) - (b.order || 0))
      : DEFAULT_MEDIA_SHOWCASE;

  // Lightbox preview state
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const isSingle = mediaList.length === 1;
  const isTwo = mediaList.length === 2;
  const isThree = mediaList.length === 3;

  return (
    <section id="media-showcase-section" className="py-12 md:py-16 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 sm:mb-12 gap-4 pb-4 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: primaryColor }}
              />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {badge}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm sm:text-base text-slate-600 mt-1.5 max-w-2xl">
                {subtitle}
              </p>
            )}
          </div>

          {buttonEnabled && (
            <button
              type="button"
              onClick={() => navigate('media-showcase')}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs text-white shadow-md hover:opacity-95 transition-all self-start md:self-end shrink-0 cursor-pointer"
              style={{ backgroundColor: primaryColor }}
            >
              <span>{buttonText}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Dynamic Media Showcase Layout */}
        {isSingle ? (
          // 🖼️ 1 PHOTO SHOWCASE: Grand Full-Width Cinematic Banner Card
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-slate-200 bg-slate-900 group min-h-[380px] sm:min-h-[480px] md:min-h-[540px] flex items-end">
            <img
              src={mediaList[0].imageUrl}
              alt={mediaList[0].title || title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 cursor-pointer"
              onClick={() => setLightboxIndex(0)}
            />
            {/* Subtle Gradient Overlays */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-transparent pointer-events-none" />

            {/* Content info overlay */}
            <div className="relative z-10 p-6 sm:p-10 md:p-12 w-full max-w-4xl text-white">
              {mediaList[0].badge && (
                <span
                  className="px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider text-white shadow-lg inline-flex items-center gap-1.5 mb-3.5"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Star className="w-3.5 h-3.5 fill-amber-300 text-amber-300" />
                  <span>{mediaList[0].badge}</span>
                </span>
              )}
              {mediaList[0].title && (
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight mb-2.5 drop-shadow-md">
                  {mediaList[0].title}
                </h3>
              )}
              {mediaList[0].caption && (
                <p className="text-slate-200 text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl drop-shadow-sm mb-4">
                  {mediaList[0].caption}
                </p>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setLightboxIndex(0)}
                  className="px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 backdrop-blur-md text-white font-bold text-xs border border-white/30 flex items-center gap-1.5 transition-all cursor-pointer shadow-md"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  <span>Enlarge Photo</span>
                </button>
                {mediaList[0].linkUrl && (
                  <a
                    href={mediaList[0].linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-1.5 transition-all shadow-md"
                  >
                    <span>Open Link</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : isTwo ? (
          // 🖼️ 2 PHOTOS SHOWCASE: Side-by-Side Dual Feature
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-7">
            {mediaList.map((item, idx) => (
              <div
                key={item.id || idx}
                className="relative rounded-2xl overflow-hidden shadow-md border border-slate-200 bg-slate-900 group aspect-4/3 sm:aspect-16/10 flex items-end"
              >
                <img
                  src={item.imageUrl}
                  alt={item.title || `${title} ${idx + 1}`}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer"
                  onClick={() => setLightboxIndex(idx)}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent pointer-events-none" />

                <div className="relative z-10 p-5 sm:p-7 w-full text-white">
                  {item.badge && (
                    <span
                      className="px-2.5 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider text-white shadow-md inline-block mb-2"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {item.badge}
                    </span>
                  )}
                  {item.title && (
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-1 drop-shadow-sm">
                      {item.title}
                    </h3>
                  )}
                  {item.caption && (
                    <p className="text-xs sm:text-sm text-slate-200 line-clamp-2 leading-relaxed">
                      {item.caption}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => setLightboxIndex(idx)}
                      className="px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur-xs text-white font-bold text-[11px] border border-white/20 flex items-center gap-1 cursor-pointer"
                    >
                      <Maximize2 className="w-3 h-3" />
                      <span>View</span>
                    </button>
                    {item.linkUrl && (
                      <a
                        href={item.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[11px] flex items-center gap-1 shadow-xs"
                      >
                        <span>Link</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : isThree ? (
          // 🖼️ 3 PHOTOS SHOWCASE: 1 Major Card + 2 Stacked Cards
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-7">
            {/* Main Featured Photo */}
            <div className="lg:col-span-2 relative rounded-2xl overflow-hidden shadow-md border border-slate-200 bg-slate-900 group aspect-16/10 lg:aspect-auto min-h-[340px] flex items-end">
              <img
                src={mediaList[0].imageUrl}
                alt={mediaList[0].title || `${title} 1`}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer"
                onClick={() => setLightboxIndex(0)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent pointer-events-none" />
              <div className="relative z-10 p-6 sm:p-8 w-full text-white">
                {mediaList[0].badge && (
                  <span
                    className="px-3 py-1 rounded-md text-xs font-extrabold uppercase tracking-wider text-white shadow-md inline-block mb-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {mediaList[0].badge}
                  </span>
                )}
                {mediaList[0].title && (
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 drop-shadow-sm">
                    {mediaList[0].title}
                  </h3>
                )}
                {mediaList[0].caption && (
                  <p className="text-xs sm:text-sm text-slate-200 max-w-xl line-clamp-3 leading-relaxed mb-3">
                    {mediaList[0].caption}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setLightboxIndex(0)}
                  className="px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur-xs text-white font-bold text-xs border border-white/20 inline-flex items-center gap-1 cursor-pointer"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  <span>Buka Foto Penuh</span>
                </button>
              </div>
            </div>

            {/* 2 Complementary Stacked Photos */}
            <div className="flex flex-col gap-6 sm:gap-7">
              {mediaList.slice(1, 3).map((item, idx) => {
                const realIdx = idx + 1;
                return (
                  <div
                    key={item.id || realIdx}
                    className="relative rounded-2xl overflow-hidden shadow-md border border-slate-200 bg-slate-900 group aspect-16/10 flex-1 flex items-end"
                  >
                    <img
                      src={item.imageUrl}
                      alt={item.title || `${title} ${realIdx + 1}`}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer"
                      onClick={() => setLightboxIndex(realIdx)}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent pointer-events-none" />
                    <div className="relative z-10 p-4 sm:p-5 w-full text-white">
                      {item.badge && (
                        <span
                          className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider text-white inline-block mb-1"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {item.badge}
                        </span>
                      )}
                      {item.title && (
                        <h4 className="text-sm sm:text-base font-bold text-white truncate drop-shadow-sm">
                          {item.title}
                        </h4>
                      )}
                      {item.caption && (
                        <p className="text-[11px] text-slate-200 line-clamp-1 mt-0.5">
                          {item.caption}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // 🖼️ 4 OR MORE PHOTOS: Balanced Modern Responsive Grid
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {mediaList.map((item, idx) => (
              <div
                key={item.id || idx}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col group"
              >
                <div className="relative aspect-4/3 overflow-hidden bg-slate-100">
                  <img
                    src={item.imageUrl}
                    alt={item.title || `Media ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer"
                    loading="lazy"
                    onClick={() => setLightboxIndex(idx)}
                  />
                  {item.badge && (
                    <span
                      className="absolute top-3 left-3 px-2 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider text-white shadow-md backdrop-blur-xs"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {item.badge}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(idx)}
                    className="absolute bottom-3 right-3 p-1.5 rounded-lg bg-slate-900/70 hover:bg-slate-900 text-white backdrop-blur-xs opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer shadow-md"
                    title="Perbesar foto"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="p-4 flex-1 flex flex-col justify-between">
                  <div>
                    {item.title && (
                      <h4 className="font-bold text-sm sm:text-base text-slate-900 group-hover:text-emerald-700 transition-colors line-clamp-1">
                        {item.title}
                      </h4>
                    )}
                    {item.caption && (
                      <p className="text-xs text-slate-500 mt-1.5 line-clamp-2 leading-relaxed">
                        {item.caption}
                      </p>
                    )}
                  </div>

                  {item.linkUrl && (
                    <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                      <a
                        href={item.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-700 hover:text-emerald-800 font-bold inline-flex items-center gap-1"
                      >
                        <span>View Details</span>
                        <ExternalLink className="w-3 h-3 text-emerald-600" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Modal for Fullscreen Photo Viewing */}
      {lightboxIndex !== null && mediaList[lightboxIndex] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-200">
          <button
            type="button"
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer z-50 shadow-lg"
            title="Close (Esc)"
          >
            <X className="w-6 h-6" />
          </button>

          {mediaList.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => setLightboxIndex((prev) => (prev !== null ? (prev - 1 + mediaList.length) % mediaList.length : 0))}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-all cursor-pointer z-50 shadow-lg"
                title="Previous Photo"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={() => setLightboxIndex((prev) => (prev !== null ? (prev + 1) % mediaList.length : 0))}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-all cursor-pointer z-50 shadow-lg"
                title="Next Photo"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <div className="max-w-5xl w-full flex flex-col items-center">
            <img
              src={mediaList[lightboxIndex].imageUrl}
              alt={mediaList[lightboxIndex].title || 'Showcase'}
              className="max-h-[75vh] w-auto max-w-full rounded-2xl object-contain shadow-2xl border border-white/10"
            />
            {(mediaList[lightboxIndex].title || mediaList[lightboxIndex].caption) && (
              <div className="text-center mt-4 text-white max-w-2xl px-4">
                {mediaList[lightboxIndex].title && (
                  <h3 className="text-lg sm:text-xl font-bold">{mediaList[lightboxIndex].title}</h3>
                )}
                {mediaList[lightboxIndex].caption && (
                  <p className="text-xs sm:text-sm text-slate-300 mt-1 leading-relaxed">
                    {mediaList[lightboxIndex].caption}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
